/**
 * DataTable — the kit's headless, virtualized data grid for large datasets.
 *
 * Built on TanStack Table (headless column/row/sort model, so the markup and
 * styling are entirely ours — the Vintage brand, not a vendor grid's look) and
 * TanStack Virtual (only the visible rows mount, so a very large book renders at
 * constant DOM cost). Pairs with a cursor-paginated infinite query: as the user
 * nears the end, `onReachEnd` fetches the next page, which appends seamlessly.
 *
 * The caller owns the `table` instance (columns, sorting mode, state) and passes
 * it in; this primitive renders the sticky header, the virtualized body, sort
 * affordances, the first-load skeleton, the empty state, and the "loading more"
 * footer. Column presentation hints (alignment, monospace figures, flex-grow)
 * ride on each column's `meta`.
 */
import { type ReactNode, useEffect, useRef } from 'react';
import {
  flexRender,
  type RowData,
  type Table as TanStackTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronUp, ChevronDown, Loader2 } from '~/components/icons';
import styles from './DataTable.module.css';

// Kit-level column presentation hints, merged into TanStack's ColumnMeta.
declare module '@tanstack/react-table' {
  // The TData/TValue params are required by the base interface signature.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Cell + header text alignment (default left). */
    readonly align?: 'left' | 'right' | 'center';
    /** Render the cell with the tabular monospace figure treatment. */
    readonly mono?: boolean;
    /** Let this column absorb extra horizontal space (otherwise fixed to `size`). */
    readonly grow?: boolean;
  }
}

/** Default uniform row height (px) — uniform rows keep virtualization measurement-free and CLS-safe. */
const DEFAULT_ROW_HEIGHT = 52;
const SKELETON_ROWS = 8;

export interface DataTableProps<T> {
  readonly table: TanStackTable<T>;
  /** Uniform row height in px (drives the virtualizer's size estimate). */
  readonly rowHeight?: number | undefined;
  /** Overrides the scroll viewport's max height (number = px; string = any CSS length). */
  readonly maxBodyHeight?: number | string | undefined;
  /** Fired when the last row scrolls into view — fetch the next page here (guard on hasNextPage). */
  readonly onReachEnd?: (() => void) | undefined;
  /** First-load state: render skeleton rows instead of the body. */
  readonly isLoading?: boolean | undefined;
  /** Next-page state: render the "loading more" footer. */
  readonly isFetchingNextPage?: boolean | undefined;
  /** Rendered in the body when there are zero rows and not loading. */
  readonly emptyState?: ReactNode | undefined;
  /** Min width before horizontal scrolling kicks in (default: sum of column sizes). */
  readonly minWidth?: number | undefined;
  readonly ariaLabel?: string | undefined;
  readonly 'data-testid'?: string | undefined;
}

export function DataTable<T>({
  table,
  rowHeight = DEFAULT_ROW_HEIGHT,
  maxBodyHeight,
  onReachEnd,
  isLoading = false,
  isFetchingNextPage = false,
  emptyState,
  minWidth,
  ariaLabel,
  'data-testid': testId,
}: DataTableProps<T>): ReactNode {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rows = table.getRowModel().rows;
  const leafColumns = table.getVisibleLeafColumns();

  const gridTemplateColumns = leafColumns
    .map((c) => (c.columnDef.meta?.grow ? `minmax(${c.getSize()}px, 1fr)` : `${c.getSize()}px`))
    .join(' ');
  const resolvedMinWidth = minWidth ?? leafColumns.reduce((sum, c) => sum + c.getSize(), 0);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });
  const virtualItems = virtualizer.getVirtualItems();

  // Infinite scroll: when the last row is within the overscan window, ask for more.
  // The query layer dedupes (hasNextPage / isFetching guards), so this can fire freely.
  const lastIndex = virtualItems.length > 0 ? virtualItems[virtualItems.length - 1]!.index : -1;
  useEffect(() => {
    if (lastIndex >= rows.length - 1 && rows.length > 0) onReachEnd?.();
  }, [lastIndex, rows.length, onReachEnd]);

  const headers = table.getHeaderGroups()[0]?.headers ?? [];
  const scrollStyle =
    maxBodyHeight !== undefined
      ? { maxHeight: typeof maxBodyHeight === 'number' ? `${maxBodyHeight}px` : maxBodyHeight }
      : undefined;

  return (
    <div className={styles.root ?? ''} data-testid={testId}>
      <div
        ref={scrollRef}
        className={styles.scroll ?? ''}
        style={scrollStyle}
        role="table"
        aria-label={ariaLabel}
      >
        {/* Sticky header — scrolls horizontally with the body, pins vertically. */}
        <div
          className={styles.headerRow ?? ''}
          style={{ gridTemplateColumns, minWidth: resolvedMinWidth }}
          role="row"
        >
          {headers.map((header) => {
            const meta = header.column.columnDef.meta;
            const sorted = header.column.getIsSorted();
            const canSort = header.column.getCanSort();
            const label = flexRender(header.column.columnDef.header, header.getContext());
            return (
              <div
                key={header.id}
                role="columnheader"
                aria-sort={
                  sorted === 'asc'
                    ? 'ascending'
                    : sorted === 'desc'
                      ? 'descending'
                      : canSort
                        ? 'none'
                        : undefined
                }
                className={cx(
                  styles.headerCell,
                  meta?.align === 'right' && styles.alignRight,
                  meta?.align === 'center' && styles.alignCenter,
                )}
              >
                {canSort ? (
                  <button
                    type="button"
                    className={styles.sortButton ?? ''}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <span>{label}</span>
                    <span
                      className={cx(styles.sortIcon, sorted && styles.sortIconActive)}
                      aria-hidden
                    >
                      {sorted === 'asc' ? (
                        <ChevronUp size={12} weight="bold" />
                      ) : (
                        <ChevronDown size={12} weight="bold" />
                      )}
                    </span>
                  </button>
                ) : (
                  <span>{label}</span>
                )}
              </div>
            );
          })}
        </div>

        {isLoading ? (
          <SkeletonBody gridTemplateColumns={gridTemplateColumns} minWidth={resolvedMinWidth} rowHeight={rowHeight} />
        ) : rows.length === 0 ? (
          <div className={styles.emptyWrap ?? ''}>{emptyState}</div>
        ) : (
          <div
            className={styles.body ?? ''}
            style={{ height: virtualizer.getTotalSize(), minWidth: resolvedMinWidth }}
          >
            {virtualItems.map((vi) => {
              const row = rows[vi.index];
              if (!row) return null;
              return (
                <div
                  key={row.id}
                  role="row"
                  data-index={vi.index}
                  data-testid="data-table-row"
                  className={styles.row ?? ''}
                  style={{
                    gridTemplateColumns,
                    height: rowHeight,
                    transform: `translateY(${vi.start}px)`,
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    return (
                      <div
                        key={cell.id}
                        role="cell"
                        className={cx(
                          styles.cell,
                          meta?.align === 'right' && styles.alignRight,
                          meta?.align === 'center' && styles.alignCenter,
                          meta?.mono && styles.mono,
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isFetchingNextPage ? (
        <div className={styles.loadingMore ?? ''} data-testid="data-table-loading-more">
          <Loader2 size={14} className={styles.spin ?? ''} aria-hidden />
        </div>
      ) : null}
    </div>
  );
}

function SkeletonBody({
  gridTemplateColumns,
  minWidth,
  rowHeight,
}: {
  readonly gridTemplateColumns: string;
  readonly minWidth: number;
  readonly rowHeight: number;
}): ReactNode {
  return (
    <div className={styles.body ?? ''} style={{ minWidth }} data-testid="data-table-loading">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <div
          key={i}
          className={cx(styles.row, styles.skeletonRow)}
          style={{ gridTemplateColumns, height: rowHeight }}
        >
          {gridTemplateColumns.split(' ').map((_col, c) => (
            <div key={c} className={styles.cell ?? ''}>
              <span className={styles.skeletonBar ?? ''} style={{ width: `${40 + ((c * 17) % 45)}%` }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Join truthy class names (CSS-module members are `string | undefined`). */
function cx(...classes: (string | false | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}
